// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { get, safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Color from "color";
import * as React from "react";
import { useState } from "react";
import styled, { css, withTheme } from "styled-components";

export type NotifyProps = MobiusStyledComponentProps<
    "div",
    {
        /** Text to display as the title of the notify. Will be ignored if `children` are passed. */
        title?: React.ReactNode;
        /** Text to display in the body of the notify. Will be ignored if `children` are passed. */
        body?: React.ReactNode;
        /** The type of message to be displayed, which governs color and iconography. */
        messageType?: "success" | "danger" | "info" | "warning";
        /** Description of type of message to be read to screenreader, to replace the iconography and color in relaying
         * the importance or valence of the message. Defaults to `messageType` value if not passed in.
         * Can be used for translations or additional specificity if necessary. */
        messageTypeString?: string;
        notifyContainerOverrides?: StyledProp;
        iconContainerOverrides?: StyledProp;
        notifyTitleOverrides?: StyledProp;
        notifyBodyOverrides?: StyledProp;
        showCloseButton?: boolean;
        css?: StyledProp<any>;
    }
>;

const NotifyContainer = styled.div<any>`
    display: flex;
    width: 100%;
    max-width: 100%;
    background: ${getColor("text.main")};
    box-shadow: 0 0 25px 0 rgba(79, 79, 119, 0.18);
    opacity: 0.8;
    padding: 10px;
    font-size: 14px;
    line-height: 19px;
    color: ${getColor("common.white")};
    ${injectCss}
`;

const IconContainer = styled.div<any>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin: auto 10px auto 0;
    ${injectCss};
`;

const NotifyTitle = styled.div<InjectableCss>`
    margin-right: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-weight: bold;
    width: 100%;
    ${injectCss}
`;

const NotifyBody = styled.div<InjectableCss>`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${injectCss}
`;

const CloseButtonContainer = styled.div<InjectableCss>`
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    position: relative;
    ${injectCss}
`;

const Notify: React.FC<NotifyProps> = ({
    messageType,
    messageTypeString,
    children,
    title,
    body,
    theme,
    notifyContainerOverrides,
    iconContainerOverrides,
    notifyTitleOverrides,
    notifyBodyOverrides,
    showCloseButton,
    ...otherProps
}: NotifyProps) => {
    const iconSrcByMessageMap = {
        success: "CheckOutlined",
        danger: "Error",
        warning: "Exclamation",
        info: "ContextualHelp",
    };
    const [visible, setVisibility] = useState(true);

    if (visible) {
        return (
            <NotifyContainer
                css={notifyContainerOverrides}
                messageType={messageType}
                data-test-selector={`notify${messageType}`}
                role="status"
                {...otherProps}
            >
                {children || (
                    <StyledWrapper
                        css={css`
                            position: relative;
                            display: flex;
                            flex-wrap: nowrap;
                            width: 100%;
                        `}
                    >
                        <IconContainer css={iconContainerOverrides} messageType={messageType} {...otherProps}>
                            <Icon
                                src={
                                    messageType === "success"
                                        ? "Check"
                                        : messageType === "danger"
                                        ? "Clear"
                                        : messageType === "info"
                                        ? "Info"
                                        : "Alert"
                                }
                                color={messageType}
                                size={20}
                            />
                        </IconContainer>
                        <StyledWrapper
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                flexGrow: 1,
                            }}
                        >
                            {title && (
                                <NotifyTitle css={notifyTitleOverrides}>
                                    <Typography data-test-selector="notifyTitle">{title}:</Typography>
                                </NotifyTitle>
                            )}
                            <NotifyBody css={notifyBodyOverrides} data-id="notify-body">
                                <VisuallyHidden>{messageTypeString || messageType}</VisuallyHidden>
                                <Typography data-test-selector="notifyBody">{body}</Typography>
                            </NotifyBody>
                        </StyledWrapper>
                        {showCloseButton && (
                            <CloseButtonContainer onClick={() => setVisibility(!visible)}>
                                <Icon
                                    src="Clear"
                                    size={6}
                                    color="common.white"
                                    css={css`
                                        margin: 4px 10px;
                                        opacity: 0.65;
                                        position: absolute;
                                        top: -6px;
                                        right: -12px;
                                    `}
                                />
                            </CloseButtonContainer>
                        )}
                    </StyledWrapper>
                )}
            </NotifyContainer>
        );
    }
    return <></>;
};

/** @component */
export default withTheme(Notify);
